/* =============================================================================
   Talk to a Designer — multi-step intake engine
   - Drives the tile stepper (show/hide .wq-step panels, progress, back/next)
   - Hydrates the "reference mural" banner from ?ref_name/&ref_handle/&ref_image
   - Assembles a ready-to-paste nano banana 2 (Gemini) prompt from the answers
   - Dual submit on the final step:
       1) Omnisend identify + track('ac_talk_to_designer_requested', {...})  [+ email consent if ticked]
       2) Native Shopify {% form 'contact' %} AJAX POST to /contact (bulletproof internal email)
   No dependencies. Degrades to a normal contact-form submit if JS/network fails.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-ttd-root]');
  if (!root) return;

  var COUNTED = ['room', 'style', 'subject', 'colors', 'size', 'details'];
  var SEQUENCE = ['landing'].concat(COUNTED).concat(['success']);
  var MAX_WALLS = 4;
  // The "something else" escape hatch on the style step — recorded for the designer,
  // but left out of the generated image prompt where it would just be noise.
  var STYLE_UNSURE = 'Not sure / something else';

  // Collect the steps that actually exist in the DOM (posted-success reload has only 'success').
  var steps = {};
  root.querySelectorAll('.wq-step[data-step]').forEach(function (el) {
    steps[el.getAttribute('data-step')] = el;
  });

  var form = root.querySelector('[data-ttd-form]');
  var errorEl = root.querySelector('[data-ttd-error]');
  var refBanner = root.querySelector('[data-ttd-refbanner]');

  var answers = { colors: [], reference: {} };
  var current = null;
  var submitting = false;
  var mode = 'full'; // 'full' (header nav) or 'customize' (arrived from a mural PDP with ?ref_*)

  /* ---- Reference banner (deep-link from a mural PDP) --------------------- */
  (function hydrateReference() {
    var p;
    try { p = new URLSearchParams(window.location.search); } catch (e) { return; }
    var name = p.get('ref_name');
    if (!name || !refBanner) return;
    var image = p.get('ref_image') || '';
    var handle = p.get('ref_handle') || '';
    answers.reference = {
      name: name,
      image: image,
      url: handle ? (window.location.origin + '/products/' + handle) : ''
    };
    var nameEl = refBanner.querySelector('[data-ttd-refname]');
    var thumbEl = refBanner.querySelector('[data-ttd-refthumb]');
    if (nameEl) nameEl.textContent = name;
    if (thumbEl && image) thumbEl.style.backgroundImage = 'url("' + image.replace(/"/g, '') + '")';
    refBanner.hidden = false;
    var clear = refBanner.querySelector('[data-ttd-refclear]');
    if (clear) clear.addEventListener('click', function () {
      // "Start from scratch instead" — drop the reference and reload into the full questionnaire.
      try { window.location.assign(window.location.pathname); }
      catch (e) { answers.reference = {}; refBanner.hidden = true; }
    });
  })();

  /* ---- Reference image upload (reuses the store's S3 app-proxy pipeline) --- */
  var upload = { url: '', promise: null, hasFile: false };

  function ttdCustomerId() {
    try {
      var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page;
      if (m && m.customerId) return String(m.customerId);
    } catch (e) {}
    var g = null;
    try { g = sessionStorage.getItem('ttd_guest_id'); } catch (e) {}
    if (!g) {
      g = 'ttd_' + String(Date.now()) + '_' + Math.random().toString(36).slice(2, 10);
      try { sessionStorage.setItem('ttd_guest_id', g); } catch (e) {}
    }
    return g;
  }

  // Upload a file to S3 via the Shopify app proxy, then resolve to a shareable URL.
  function uploadToS3(file) {
    var bucket = 'shopify-applied-coverings';
    return fetch('/apps/s3/api/upload-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-S3-Bucket': bucket },
      body: JSON.stringify({
        fileName: file.name.replace(/\s+/g, '_'),
        contentType: file.type,
        fileSize: file.size,
        customerId: ttdCustomerId(),
        source: 'Talk to a Designer'
      })
    })
    .then(function (r) { if (!r.ok) throw new Error('upload-url ' + r.status); return r.json(); })
    .then(function (d) {
      if (!d || !d.success || !d.uploadUrl) throw new Error('no upload url');
      var key = d.key, bkt = d.bucket || bucket;
      return fetch(d.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } })
        .then(function (pr) { if (!pr.ok) throw new Error('s3 put ' + pr.status); return { key: key, bucket: bkt }; });
    })
    .then(function (o) {
      // The bucket is NOT publicly readable (a direct object URL 403s from an email click),
      // so fetch a presigned (7-day) download URL via the same app proxy the configurator uses.
      // Pass the RAW key (slashes intact) in the path — that's how getDownloadUrl works.
      var direct = 'https://' + o.bucket + '.s3.us-west-2.amazonaws.com/' + o.key;
      return fetch('/apps/s3/api/download/' + o.key, {
        method: 'GET', headers: { 'X-S3-Bucket': o.bucket, 'x-expires-in': 604800 }
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (dd) { return (dd && dd.downloadUrl) ? dd.downloadUrl : direct; })
        .catch(function () { return direct; });
    });
  }

  (function setupUpload() {
    var input = root.querySelector('[data-ttd-upload]');
    if (!input) return;
    var zone = root.querySelector('[data-ttd-upload-zone]');
    var preview = root.querySelector('[data-ttd-upload-preview]');
    var thumb = root.querySelector('[data-ttd-upload-thumb]');
    var nameEl = root.querySelector('[data-ttd-upload-name]');
    var statusEl = root.querySelector('[data-ttd-upload-status]');
    var removeBtn = root.querySelector('[data-ttd-upload-remove]');
    var MAX = 20 * 1024 * 1024;

    function setStatus(msg, kind) {
      if (!statusEl) return;
      statusEl.textContent = msg || '';
      statusEl.className = 'ttd-upload__status' + (kind ? ' is-' + kind : '');
    }

    function reset() {
      upload = { url: '', promise: null, hasFile: false };
      input.value = '';
      if (preview) preview.hidden = true;
      if (zone) zone.hidden = false;
      if (thumb) thumb.style.backgroundImage = '';
      setStatus('');
    }

    function handleFile(file) {
      if (!file) return;
      if (!/^image\//.test(file.type)) { if (zone) zone.hidden = false; if (preview) preview.hidden = true; setStatus('Please choose an image file.', 'error'); return; }
      if (file.size > MAX) { setStatus('Image is too large (max 20 MB).', 'error'); return; }
      upload.hasFile = true;
      upload.url = '';
      if (zone) zone.hidden = true;
      if (preview) preview.hidden = false;
      if (nameEl) nameEl.textContent = file.name;
      try {
        var reader = new FileReader();
        reader.onload = function (e) { if (thumb) thumb.style.backgroundImage = "url('" + e.target.result + "')"; };
        reader.readAsDataURL(file);
      } catch (e) {}
      setStatus('Uploading…');
      upload.promise = uploadToS3(file)
        .then(function (url) { upload.url = url; setStatus('Attached ✓', 'done'); return url; })
        .catch(function (err) {
          console.error('Reference upload failed:', err);
          upload.url = '';
          setStatus('Upload failed — you can still submit and we’ll follow up for the image.', 'error');
          return '';
        });
    }

    input.addEventListener('change', function () { handleFile(input.files && input.files[0]); });
    if (removeBtn) removeBtn.addEventListener('click', reset);
    if (zone) {
      ['dragenter', 'dragover'].forEach(function (ev) { zone.addEventListener(ev, function (e) { e.preventDefault(); zone.classList.add('is-dragover'); }); });
      ['dragleave', 'drop'].forEach(function (ev) { zone.addEventListener(ev, function (e) { e.preventDefault(); zone.classList.remove('is-dragover'); }); });
      zone.addEventListener('drop', function (e) {
        var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (f) { try { input.files = e.dataTransfer.files; } catch (er) {} handleFile(f); }
      });
    }
  })();

  /* ---- Wall size: up to 4 walls, ft+in or inches-only --------------------- */
  var wallsWrap = root.querySelector('[data-ttd-walls]');

  function wallRow(i) { return wallsWrap && wallsWrap.querySelector('[data-ttd-wall="' + i + '"]'); }

  function visibleWalls() {
    var out = [];
    for (var i = 1; i <= MAX_WALLS; i++) {
      var row = wallRow(i);
      if (row && !row.hidden) out.push(i);
    }
    return out;
  }

  // Only the last visible wall offers "Remove", so the numbering never gets gaps.
  function syncWalls() {
    if (!wallsWrap) return;
    var shown = visibleWalls();
    var last = shown[shown.length - 1];
    for (var i = 2; i <= MAX_WALLS; i++) {
      var row = wallRow(i);
      if (!row) continue;
      var btn = row.querySelector('[data-ttd-wall-remove]');
      if (btn) btn.hidden = (i !== last);
    }
    var add = root.querySelector('[data-ttd-add-wall]');
    if (add) add.hidden = shown.length >= MAX_WALLS;
  }

  function clearWall(row) {
    if (!row) return;
    row.querySelectorAll('[data-ttd-size]').forEach(function (i) { i.value = ''; });
  }

  function resetWalls() {
    if (!wallsWrap) return;
    for (var i = 1; i <= MAX_WALLS; i++) {
      var row = wallRow(i);
      if (!row) continue;
      clearWall(row);
      if (i > 1) row.hidden = true;
    }
    syncWalls();
  }

  function setUnit(unit) {
    if (!wallsWrap) return;
    wallsWrap.setAttribute('data-unit', unit);
    wallsWrap.querySelectorAll('[data-ttd-unit-group]').forEach(function (g) {
      g.hidden = g.getAttribute('data-ttd-unit-group') !== unit;
    });
    root.querySelectorAll('[data-ttd-unit]').forEach(function (b) {
      var on = b.getAttribute('data-ttd-unit') === unit;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function activeUnit() {
    return (wallsWrap && wallsWrap.getAttribute('data-unit')) || 'ftin';
  }

  function sizeValue(key) {
    var el = root.querySelector('[data-ttd-size="' + key + '"]');
    return el ? el.value : '';
  }

  function trimNum(n) { return String(Math.round(n * 100) / 100); }

  function dimText(i, dim) {
    if (activeUnit() === 'in') {
      var total = parseFloat(sizeValue('w' + i + '_' + dim + '_total')) || 0;
      return total ? trimNum(total) + ' in' : '';
    }
    var ft = parseInt(sizeValue('w' + i + '_' + dim + '_ft'), 10) || 0;
    var inch = parseFloat(sizeValue('w' + i + '_' + dim + '_in')) || 0;
    if (!ft && !inch) return '';
    var s = '';
    if (ft) s += ft + ' ft';
    if (inch) s += (s ? ' ' : '') + trimNum(inch) + ' in';
    return s;
  }

  // Each filled-in wall becomes "10 ft wide × 8 ft tall"; the wall number is only
  // prefixed once there's more than one, so single-wall briefs read exactly as before.
  function collectWalls() {
    return visibleWalls().map(function (i) {
      var w = dimText(i, 'width');
      var h = dimText(i, 'height');
      if (!w && !h) return null;
      var text = (w && h) ? (w + ' wide × ' + h + ' tall') : (w ? w + ' wide' : h + ' tall');
      return { index: i, text: text };
    }).filter(Boolean);
  }

  function formatSize(walls) {
    if (!walls.length) return '';
    if (walls.length === 1) return walls[0].text;
    return walls.map(function (o) { return 'Wall ' + o.index + ': ' + o.text; }).join('; ');
  }

  function wallPhrase(count) { return count > 1 ? 'walls measuring ' : 'a wall '; }

  if (wallsWrap) syncWalls();

  /* ---- Example prompt marquees (step 3) ---------------------------------- */
  // Pause the drift while a finger is on the row so the pills are tappable.
  root.querySelectorAll('[data-ttd-marquee]').forEach(function (m) {
    m.addEventListener('touchstart', function () { m.classList.add('is-paused'); }, { passive: true });
    ['touchend', 'touchcancel'].forEach(function (ev) {
      m.addEventListener(ev, function () {
        setTimeout(function () { m.classList.remove('is-paused'); }, 1200);
      }, { passive: true });
    });
  });

  /* ---- Step navigation --------------------------------------------------- */
  function presentSteps() { return SEQUENCE.filter(function (n) { return steps[n]; }); }

  function showStep(name) {
    if (!steps[name]) return;
    Object.keys(steps).forEach(function (n) {
      var el = steps[n];
      var active = n === name;
      el.hidden = !active;
      el.classList.toggle('wq-step--active', active);
    });
    current = name;

    // Progress
    var idx = COUNTED.indexOf(name);
    if (idx > -1) {
      var pct = Math.round(((idx + 1) / COUNTED.length) * 100);
      var txt = steps[name].querySelector('[data-ttd-progress-text]');
      var bar = steps[name].querySelector('[data-ttd-progress-bar]');
      if (txt) txt.textContent = 'Step ' + (idx + 1) + ' of ' + COUNTED.length;
      if (bar) bar.style.width = pct + '%';
    }

    // Focus first input on text steps for a smoother flow
    var firstInput = steps[name].querySelector('.ttd-input');
    if (firstInput && (name === 'subject' || name === 'details')) {
      try { firstInput.focus({ preventScroll: true }); } catch (e) { firstInput.focus(); }
    }

    if (name !== 'landing') scrollToTop();
  }

  function scrollToTop() {
    var top = root.getBoundingClientRect().top + window.pageYOffset - 24;
    if (window.pageYOffset > top || (top - window.pageYOffset) > window.innerHeight) {
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  function go(delta) {
    var order = presentSteps();
    var i = order.indexOf(current);
    var next = order[i + delta];
    if (next) showStep(next);
  }

  function advanceFrom(name) {
    var order = presentSteps();
    var i = order.indexOf(name);
    if (i > -1 && order[i + 1]) showStep(order[i + 1]);
  }

  /* ---- Interactions ------------------------------------------------------ */
  root.addEventListener('click', function (e) {
    var t = e.target.closest('button');
    if (!t || !root.contains(t)) return;

    if (t.hasAttribute('data-ttd-start')) { showStep('room'); return; }
    if (t.hasAttribute('data-ttd-back')) { go(-1); return; }
    if (t.hasAttribute('data-ttd-next')) { go(1); return; }

    if (t.hasAttribute('data-ttd-skip')) { // wall size "not sure yet"
      answers.size = null;
      resetWalls();
      advanceFrom('size');
      return;
    }

    // Wall size: unit toggle + add/remove walls
    var unit = t.getAttribute('data-ttd-unit');
    if (unit) { setUnit(unit); return; }

    if (t.hasAttribute('data-ttd-add-wall')) {
      var shown = visibleWalls();
      var next = wallRow(shown.length + 1);
      if (next) {
        next.hidden = false;
        syncWalls();
        var firstField = next.querySelector('[data-ttd-size]');
        if (firstField) { try { firstField.focus({ preventScroll: true }); } catch (e) { firstField.focus(); } }
      }
      return;
    }

    if (t.hasAttribute('data-ttd-wall-remove')) {
      var row = t.closest('[data-ttd-wall]');
      if (row) { clearWall(row); row.hidden = true; syncWalls(); }
      return;
    }

    // Single-select tile -> record + auto-advance
    var single = t.getAttribute('data-ttd-answer');
    if (single) {
      answers[single] = t.getAttribute('data-value') || t.dataset.value || '';
      var parentStep = t.closest('.wq-step');
      if (parentStep) {
        parentStep.querySelectorAll('[data-ttd-answer]').forEach(function (b) { b.classList.remove('is-selected'); });
      }
      t.classList.add('is-selected');
      setTimeout(function () { advanceFrom(single); }, 160);
      return;
    }

    // Multi-select tile (colors) -> toggle
    var multi = t.getAttribute('data-ttd-multi');
    if (multi) {
      var val = t.getAttribute('data-value') || t.dataset.value || '';
      if (!answers[multi]) answers[multi] = [];
      var pos = answers[multi].indexOf(val);
      if (pos > -1) { answers[multi].splice(pos, 1); t.classList.remove('is-selected'); }
      else { answers[multi].push(val); t.classList.add('is-selected'); }
      return;
    }

    // Scrolling example -> drop the whole sentence into the subject input to edit
    if (t.hasAttribute('data-ttd-example')) {
      var input = root.querySelector('[data-ttd-input="subject"]');
      if (input) {
        input.value = t.getAttribute('data-value') || t.dataset.value || '';
        input.focus();
        try { input.setSelectionRange(input.value.length, input.value.length); } catch (e) {}
      }
      return;
    }
  });

  /* ---- Prompt + brief assembly ------------------------------------------ */
  function buildPrompt(size, wallCount) {
    if (mode === 'customize') {
      var ref = answers.reference || {};
      var cp = 'Customize the existing wall mural "' + (ref.name || 'the selected mural') + '"';
      if (ref.url) cp += ' (' + ref.url + ')';
      cp += '.';
      if (answers.edits) cp += ' Requested changes: ' + answers.edits + '.';
      if (size) cp += ' Sized for ' + wallPhrase(wallCount) + size + '.';
      cp += ' Keep it print-ready as seamless, high-resolution wallpaper with tileable edges.';
      return cp;
    }
    var style = (answers.style && answers.style !== STYLE_UNSURE) ? answers.style : '';
    var subject = (answers.subject || '').trim() ||
      (style ? style.toLowerCase() + ' scene' : 'a custom scene');
    var lead = 'A photorealistic wall mural';
    if (answers.room) lead += ' for a ' + answers.room.toLowerCase();
    lead += ', featuring ' + subject + '.';
    var parts = [lead];
    if (style) parts.push('Style: ' + style + '.');
    if (answers.colors && answers.colors.length) parts.push('Color palette: ' + answers.colors.join(', ') + '.');
    if (answers.reference && answers.reference.name) parts.push('Inspired by the look of "' + answers.reference.name + '".');
    var tail = 'Designed to print as seamless wallpaper';
    if (size) tail += ', sized for ' + wallPhrase(wallCount) + size;
    tail += '. High resolution, tileable edges, professional interior-design quality.';
    parts.push(tail);
    if (answers.notes) parts.push('Additional notes: ' + answers.notes + '.');
    return parts.join(' ');
  }

  function buildBody(size, prompt, wallCount) {
    var sizeLabel = (wallCount > 1 ? 'Wall sizes: ' : 'Wall size: ');
    var L = [];
    if (mode === 'customize') {
      var ref = answers.reference || {};
      L.push('New CUSTOMIZE request via Talk to a Designer (from a mural product page).');
      L.push('');
      L.push('Reference mural: ' + (ref.name || '') + (ref.url ? ' (' + ref.url + ')' : ''));
      if (answers.edits) L.push('Requested changes: ' + answers.edits);
      if (size) L.push(sizeLabel + size);
      if (upload && upload.url) L.push('Reference image (uploaded): ' + upload.url);
      L.push('');
      L.push('--- Ready-to-paste nano banana 2 prompt ---');
      L.push(prompt);
      return L.join('\n');
    }
    L.push('New custom mural request via Talk to a Designer.');
    L.push('');
    if (answers.room) L.push('Room: ' + answers.room);
    if (answers.style) L.push('Style: ' + answers.style);
    if (answers.subject) L.push('Subject: ' + answers.subject);
    if (answers.colors && answers.colors.length) L.push('Colors: ' + answers.colors.join(', '));
    if (size) L.push(sizeLabel + size);
    if (answers.reference && answers.reference.name) {
      L.push('Reference mural: ' + answers.reference.name + (answers.reference.url ? ' (' + answers.reference.url + ')' : ''));
    }
    if (answers.notes) L.push('Notes: ' + answers.notes);
    if (upload && upload.url) L.push('Reference image (uploaded): ' + upload.url);
    L.push('');
    L.push('--- Ready-to-paste nano banana 2 prompt ---');
    L.push(prompt);
    return L.join('\n');
  }

  function setHidden(key, val) {
    var el = root.querySelector('[data-ttd-hidden="' + key + '"]');
    if (el) el.value = val == null ? '' : val;
  }

  /* ---- Submit ------------------------------------------------------------ */
  function collectContact() {
    var g = function (sel) { var el = root.querySelector(sel); return el ? el.value.trim() : ''; };
    answers.name = g('[name="contact[name]"]');
    answers.email = g('[name="contact[email]"]');
    answers.phone = g('[name="contact[phone]"]');
    var subjEl = root.querySelector('[data-ttd-input="subject"]');
    answers.subject = subjEl ? subjEl.value.trim() : '';
    var notesEl = root.querySelector('[data-ttd-input="notes"]');
    answers.notes = notesEl ? notesEl.value.trim() : '';
    var editsEl = root.querySelector('[data-ttd-input="edits"]');
    answers.edits = editsEl ? editsEl.value.trim() : '';
    var consentEl = root.querySelector('[data-ttd-input="consent"]');
    answers.consent = consentEl ? consentEl.checked : false;
  }

  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

  /* ---- Omnisend: identify + track ac_talk_to_designer_requested ----------------
     Omnisend's track() is a plain fetch with no keepalive, and this form leaves the page on
     submit (native POST), so a call made at submit time can be cancelled mid-flight. Instead:
       1) at submit we only STASH the payload in sessionStorage (nothing is sent);
       2) the server-rendered success view (?contact_posted=true) reads it, deletes it FIRST
          (so refresh/back can never re-fire), then sends identify + track from a page that
          stays put.
     Email consent is passed ONLY when the marketing box was ticked at submit (consents + optIns on
     the event contact); an unticked submit sends no consent/optIn/optOut/status fields, so an
     existing unsubscribed contact is never flipped back. Every step is wrapped so a
     missing/blocked/erroring Omnisend can never affect the native form post. */
  var OMNI_EVENT = 'ac_talk_to_designer_requested';
  var OMNI_KEY = 'ttd_omnisend_pending';
  var OMNI_CONSENT_SOURCE = 'Talk to a Designer form';
  var OMNI_MAX_AGE = 30 * 60 * 1000; // ignore a stash older than 30 min (e.g. an abandoned failed post)

  // Mirrors the event's customer_first_name ("title-cased at source"); mixed-case input is left as typed.
  function omniFirstName(s) {
    if (!s || (s !== s.toLowerCase() && s !== s.toUpperCase())) return s || '';
    return s.toLowerCase().replace(/(^|[\s'’-])(\S)/g, function (m, a, b) { return a + b.toUpperCase(); });
  }

  // identify is a method on the snippet's global (not a queue command); track is a queue command.
  // If the snippet hasn't finished loading, `omnisend` is still a plain array queue: wait up to ~6s
  // for identifyContact to appear, then queue the track call either way (it replays on load).
  function sendOmnisend(p) {
    try {
      window.omnisend = window.omnisend || []; // standard snippet queue: calls made before the snippet loads still run
      var ident = { email: p.email };
      if (p.firstName) ident.firstName = p.firstName;
      if (p.lastName) ident.lastName = p.lastName;
      if (p.phone) ident.phone = p.phone;
      var tracked = false;
      var track = function () {
        if (tracked) return;
        tracked = true;
        try {
          var contact = { email: p.email };
          if (p.consent) {
            var nowIso = new Date().toISOString();
            contact.consents = [{ channel: 'email', createdAt: nowIso, source: OMNI_CONSENT_SOURCE, userAgent: navigator.userAgent }];
            contact.optIns = [{ channel: 'email', createdAt: nowIso, source: OMNI_CONSENT_SOURCE }];
          }
          (window.omnisend = window.omnisend || []).push(['track', OMNI_EVENT, {
            origin: 'api', contact: contact, properties: p.props
          }]);
        } catch (e) { /* noop */ }
      };
      var identify = function () {
        var o = window.omnisend;
        if (!o || typeof o.identifyContact !== 'function') return false;
        try { o.identifyContact(ident); } catch (e) { /* noop */ }
        return true; // attempted once; never retried
      };
      if (identify()) { track(); return; }
      var tries = 0;
      var timer = setInterval(function () {
        tries++;
        if (identify() || tries >= 24) { clearInterval(timer); track(); }
      }, 250);
    } catch (e) { /* noop */ }
  }

  // Runs at submit. Stashes only; never sends, never throws.
  function stashOmnisend(size, prompt) {
    var p;
    try {
      if (!answers.email) return;
      var parts = (answers.name || '').split(/\s+/).filter(Boolean);
      var first = parts[0] || '';
      p = {
        t: Date.now(),
        email: answers.email,
        firstName: first,
        lastName: parts.slice(1).join(' '),
        phone: answers.phone || '',
        consent: !!answers.consent, // marketing box state at submit
        // The registered event schema's properties, incl. customer_first_name.
        props: {
          source: root.getAttribute('data-source') || 'Talk to a Designer',
          mode: mode,
          requested_changes: answers.edits || '',
          room: answers.room || '',
          style: answers.style || '',
          subject: answers.subject || '',
          colors: (answers.colors || []).join(', '),
          wall_size: size || '',
          notes: answers.notes || '',
          reference_mural: (answers.reference && answers.reference.name) || '',
          reference_image: (answers.reference && answers.reference.image) || '',
          reference_url: (answers.reference && answers.reference.url) || '',
          reference_upload_url: (upload && upload.url) || '',
          generated_prompt: prompt,
          name: answers.name || '',
          phone: answers.phone || '',
          customer_first_name: omniFirstName(first)
        }
      };
    } catch (e) { return; }
    try { sessionStorage.setItem(OMNI_KEY, JSON.stringify(p)); }
    catch (e) { sendOmnisend(p); } // storage blocked: best-effort send now (navigation may cancel it)
  }

  // Success view only: send the stashed submission exactly once.
  function flushOmnisend() {
    var raw, p;
    try {
      raw = sessionStorage.getItem(OMNI_KEY);
      if (!raw) return;
      sessionStorage.removeItem(OMNI_KEY); // delete BEFORE sending => refresh/back can't re-fire
      p = JSON.parse(raw);
    } catch (e) { return; }
    if (!p || !p.email || !p.t || (Date.now() - p.t) > OMNI_MAX_AGE) return;
    sendOmnisend(p);
  }

  function showSuccess() {
    var nameSpan = root.querySelector('[data-ttd-success-name]');
    if (nameSpan && answers.name) nameSpan.textContent = ', ' + answers.name.split(' ')[0];
    showStep('success');
  }

  function populateAndFire() {
    collectContact();
    var walls = collectWalls();
    var size = formatSize(walls);
    var prompt = buildPrompt(size, walls.length);
    answers.size = size;

    // Populate hidden fields carried in the native contact email
    setHidden('body', buildBody(size, prompt, walls.length));
    setHidden('room', answers.room || '');
    setHidden('style', answers.style || '');
    setHidden('subject', answers.subject || '');
    setHidden('colors', (answers.colors || []).join(', '));
    setHidden('wall_size', size || '');
    setHidden('reference_mural', (answers.reference && answers.reference.name) || '');
    setHidden('reference_image', (answers.reference && answers.reference.image) || '');
    setHidden('reference_url', (answers.reference && answers.reference.url) || '');
    setHidden('generated_prompt', prompt);
    setHidden('edits', answers.edits || '');
    setHidden('reference_upload', (upload && upload.url) || '');

    stashOmnisend(size, prompt);
  }

  function setSubmitLoading(on) {
    var btn = root.querySelector('[data-ttd-submit]');
    var loading = root.querySelector('[data-ttd-submit-loading]');
    if (btn) { btn.classList.toggle('is-loading', on); btn.disabled = on; }
    if (loading) loading.hidden = !on;
  }

  // Submit as a GENUINE native form post so Shopify's invisible spam-protection captcha
  // auto-solves (a background/AJAX post is rejected 400). The Omnisend payload is stashed first
  // (sent from the success view), then Shopify processes the contact
  // form and redirects back here with ?contact_posted=true, where the server-rendered
  // success panel ({% if form.posted_successfully? %}) shows.
  var nativePrepared = false;
  if (form) {
    form.addEventListener('submit', function (e) {
      if (nativePrepared) return; // second pass (after an upload wait) — let Shopify + native submit run
      // Required name/email are enforced by the inputs' HTML5 validation, so reaching the
      // submit handler means they're already valid. Only hold the submit if a reference image
      // is still uploading, so its URL makes it into the brief.
      if (upload && upload.hasFile && upload.promise && !upload.url) {
        e.preventDefault();
        if (submitting) return;
        submitting = true;
        setSubmitLoading(true);
        upload.promise.finally(function () {
          populateAndFire();
          nativePrepared = true;
          try { if (form.requestSubmit) { form.requestSubmit(); } else { form.submit(); } }
          catch (err) { form.submit(); }
        });
        return;
      }
      // Normal path: genuine user submit — populate fields + stash Omnisend, then let it post natively.
      populateAndFire();
      setSubmitLoading(true);
    });
  }

  // Customize mode = arrived from a mural PDP (reference present). Collapse to one page.
  function enterCustomize() {
    root.querySelectorAll('[data-ttd-customize-only]').forEach(function (el) { el.hidden = false; });
    root.querySelectorAll('[data-ttd-full-only]').forEach(function (el) { el.hidden = true; });
    var prog = steps.details && steps.details.querySelector('[data-ttd-progress]');
    if (prog) prog.hidden = true;
    var back = root.querySelector('[data-ttd-details-back]');
    if (back) back.style.display = 'none';
    var heading = root.querySelector('[data-ttd-customize-heading]');
    if (heading && answers.reference && answers.reference.name) {
      var nm = answers.reference.name;
      if (nm.length > 42 && nm.indexOf(' - ') > -1) nm = nm.split(' - ')[0]; // trim "Name - long descriptor" titles
      heading.textContent = 'Make ' + nm + ' your own';
    }
    showStep('details');
  }

  mode = (answers.reference && answers.reference.name) ? 'customize' : 'full';

  // Kick off: if we reloaded straight into the success panel (non-JS post), leave it be.
  if (steps.success && steps.success.classList.contains('wq-step--active') && !steps.landing) {
    current = 'success';
    flushOmnisend(); // posted-success view: send the stashed Omnisend identify + track (once)
  } else if (mode === 'customize' && steps.details) {
    enterCustomize();
  } else if (steps.landing) {
    showStep('landing');
  }
})();
